import React from 'react';
import TimeAgo from 'react-timeago';

import {PaginationScroll} from 'Components/Partials';
import {ProfilePic, handleMessagePreview, isSameChat, isSameChatCheck} from 'Components/Networking/Sub';

import ChatsApi from 'Services/Api/Networking/Chats';

import {User, Socket} from 'Services';
import {translation} from 'Services/TranslationHelpers';
import {mergeObjectsIntoPaginationData} from 'Services/BaseHelpers';

export default class Chats extends React.Component {
    /**
     * @method constructor
     */
    constructor() {
        super();
        this.selectedChatRef = React.createRef();
    }

    /**
     * @var state
     * @type {{chats: null|object}}
     */
    state = {
        chats: null,
    }

    /**
     * @method componentDidMount
     */
    componentDidMount() {
        const {selected_chat} = this.props;

        this.subscribeToBroadcasts();

        if (selected_chat) {
            this.handleSelectedChat(selected_chat);
        }
    };

    /**
     * @method componentDidUpdate
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const {selected_chat} = this.props;

        if (selected_chat && prevProps.selected_chat !== selected_chat) {
            this.scrollToSelectedChat();

            this.handleSelectedChat(selected_chat);
        }
    }

    /**
     * @method handleSelectedChat
     * @param {object} selected_chat
     */
    handleSelectedChat = (selected_chat) => {
        /*
        TODO
        PaginationScroll resets the data when loading the first page therefore we have to wait
        until after that page has loaded to add this chat. Used a hacky setTimeout solution.
         */
        setTimeout(() => {
            this.handleNewChat(selected_chat);
        }, 500);
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount() {
        this.unsubscribeFromBroadcasts();
    }

    /**
     * @method subscribeToBroadcasts
     */
    subscribeToBroadcasts = () => {
        // Listen for new messages.
        Socket.getConnection().private('networking.' + User.id)
            .listen('NetworkingMessagePosted', (e) => {
                // If message to a group then always display it on the group,
                // else display it on the other chat
                if (e.message.message_to_type !== 'App\\Models\\User') {
                    this.handleNewChat(e.message.message_to, e.message);
                } else {
                    this.handleNewChat(
                        User.id === e.message.user_from_id ?
                            e.message.message_to :
                            e.message.user_from,
                        e.message
                    );
                }
            });
    }

    /**
     * @method unsubscribeFromBroadcasts
     */
    unsubscribeFromBroadcasts = () => {
        // Stop listening for new messages.
        Socket.getConnection().leave('networking.' + User.id);
    }

    /**
     * @method handleNewChat
     * @param {object} chat
     * @param {object} last_message
     * @return {Promise<void>}
     */
    handleNewChat = (chat, last_message = null) => {
        const {chats} = this.state;
        const {selected_chat} = this.props;

        // Update last_message.
        if (last_message) {
            chat.last_message = last_message;
        }

        // If selected_chat then mark as read.
        if (selected_chat && isSameChat(selected_chat, chat) && chat.last_message) {
            chat.last_message.read_at = true;
        }

        // Update the chats.
        if (chats.data.filter(o => isSameChat(o, chat)).length === 0) {
            // Chat is not yet in the list so add it.
            return this.setState({
                chats: mergeObjectsIntoPaginationData(chats, [chat], false, isSameChatCheck())
            });
        } else {
            // Chat is already in the list so update the last_message.
            return this.setState({
                chats: {
                    data: chats.data.map((o) => {
                        if (isSameChat(o, chat)) {
                            o.last_message = chat.last_message;
                        }

                        return o;
                    }),
                    meta: chats.meta
                }
            });
        }
    }

    /**
     * @method scrollToSelectedChat
     */
    scrollToSelectedChat = () => {
        if (this.selectedChatRef.current !== null) {
            this.selectedChatRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }

    /**
     * @method sortChats
     * @param {object} chat_a
     * @param {object} chat_b
     * @return {integer}
     */
    sortChats = (chat_a, chat_b) => {
        // If both have no messages then leave as is.
        if (!chat_a.last_message && !chat_b.last_message) {
            return 1;
        }

        // Put chats with no messages on top.
        if (!chat_a.last_message) {
            return -1;
        }

        if (!chat_b.last_message) {
            return 1;
        }

        // If both have same message time then leave as is.
        if (chat_a.last_message.created_at.iso_string === chat_b.last_message.created_at.iso_string) {
            return 1;
        }

        // Order by last_message date.
        var x = new Date(chat_a.last_message.created_at.iso_string);
        var y = new Date(chat_b.last_message.created_at.iso_string);

        return y - x;
    }

    /**
     * @method render
     * @return {*}
     */
    render() {
        const {chats} = this.state;

        return (
            <div className="h-full flex flex-col border-r-0 md:border-r-2 -mt-2 border-app-blue">
                <div className="h-20 hidden lg:flex flex-col justify-center">
                    <h2 className="ml-2 text-lg text-app-blue ml-3">
                        Inbox
                    </h2>
                </div>

                <PaginationScroll
                    scrollDown={true}
                    apiCall={(page) => ChatsApi.get(null, {page})}
                    updateData={(data) => this.setState({chats: data})}
                    data={chats}
                    containerClassName="bg-networking-primary border border-networking-primary shadow-sm"
                    sameObjectCheck={isSameChatCheck()}
                >
                    {chats && chats.data.length === 0 &&
                        <div className="mt-3 p-4 text-center font-bold text-app-blue">
                            {translation('networking_frontend', 'connect_message')}
                        </div>
                    }

                    {chats && chats.data.sort((a, b) => {
                        return this.sortChats(a, b)
                    }).map((chat, i) => (this.renderChat(chat)))}
                </PaginationScroll>
            </div>
        )
    }

    /**
     * @method renderChat
     * @param {object} chat
     * @return {*}
     */
    renderChat(chat) {
        const {selected_chat, setSelectedChat} = this.props;

        let is_selected_chat = (selected_chat && isSameChat(selected_chat, chat));
        let is_unread = (chat.last_message && chat.last_message.user_from_id === chat.id && chat.last_message.read_at === null);

        return (
            <div
                className={`
                    w-full p-4 cursor-pointer border-b-2 bg-networking-active border-white
                    ${is_unread ? 'border-networking-primary bg-networking-active' : 'bg-transparent'}
                `}

                onClick={() => setSelectedChat(chat)}
                ref={is_selected_chat ? this.selectedChatRef : null}
                key={chat.type + '_' + chat.id}
            >
                <div className="flex flex-col lg:flex-row justify-between">
                    <div className="flex flex-col lg:flex-row">
                        <div className="my-auto mx-auto lg:mx-0 lg:mr-2">
                            <ProfilePic user={chat}/>
                        </div>

                        <div className="mx-auto lg:mx-0">
                            <p className="font-bold text-center lg:text-left mt-2 lg:mt-0 text-networking-secondary-text">
                                { chat.name }
                            </p>

                            <p className="font-thin text-center lg:text-left mt-2 lg:mt-0 text-networking-secondary-text">
                                { chat.company_name }
                            </p>

                            <p className="font-thin text-center lg:text-left mt-2 lg:mt-0 text-networking-secondary-text">
                                { chat.job_title }
                            </p>


                            { chat.last_message &&
                                <p className="mt-2 text-sm text-center lg:text-left">
                                    { handleMessagePreview ( chat.last_message.message ) }
                                </p>
                            }
                        </div>
                    </div>

                    <div className="ml-0 lg:ml-2 mt-1 lg:mt-0">
                        { chat.last_message &&
                            <>
                                <p className="mt-1 font-bold text-center lg:text-right text-ehite text-xs">
                                    <TimeAgo date={chat.last_message.created_at.iso_string}/>
                                </p>

                                {is_unread &&
                                    <p className="mt-2 lg:mt-1 text-sm text-center lg:text-right font-bold">
                                        {translation('networking_frontend', 'unread_message')}
                                    </p>
                                }
                            </>
                        }
                    </div>
                </div>
            </div>
        );
    }
}
